import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Auth({ mode }) {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const { login, register, user, refresh } = useAuth();
  const isRegister = mode === "register";
  const [tier, setTier] = useState(sp.get("tier") || "explorer");
  const [form, setForm] = useState({ email: "", password: "", full_name: "", company: "", country: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [otpStep, setOtpStep] = useState(false);
  const [code, setCode] = useState("");

  useEffect(() => { if (user) nav("/dashboard"); }, [user, nav]);
  useEffect(() => {
    import("@/lib/api").then(({ default: api }) =>
      api.get("/auth/otp/status").then(({ data }) => setSmsEnabled(data.sms_enabled)).catch(() => {}));
  }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = isRegister
        ? await register({ ...form, membership_tier: tier })
        : await login(form.email, form.password);
      if (data.otp_required) {
        setOtpStep(true);
        toast.info(data.message || "Enter the code we sent to your phone");
        return;
      }
      toast.success(isRegister ? "Welcome to Virtus Diamond Registry" : "Signed in");
      nav("/dashboard");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { default: api } = await import("@/lib/api");
      await api.post("/auth/otp/verify", { email: form.email, code });
      await refresh();
      toast.success("Phone verified — welcome");
      nav("/dashboard");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  const tiers = [
    ["explorer", "$29/mo"], ["trader", "$99/mo"], ["institutional", "$299/mo"],
  ];

  return (
    <div className="min-h-screen bg-bb-bg text-bb-text flex items-center justify-center relative px-5 py-12 bb-hero-texture">
      <div className="absolute inset-0 bb-grid-bg opacity-40" />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="relative w-full max-w-md">
        <div className="flex justify-center mb-8"><Link to="/"><Logo size={26} /></Link></div>
        <div className="bb-glass bb-glass-gold rounded-sm p-8">
          <h1 className="font-serif text-3xl mb-1">{otpStep ? "Verify Your Phone" : isRegister ? "Create Account" : "Sign In"}</h1>
          <p className="text-bb-muted text-sm mb-6">
            {otpStep ? "One account, one device. Enter the 6-digit code we sent by SMS."
              : isRegister ? "Begin your 7-day free trial." : "Access your intelligence terminal."}
          </p>
          {otpStep ? (
            <form onSubmit={verifyCode} className="space-y-4" data-testid="otp-form">
              <Field label="Verification Code" value={code} onChange={(e) => setCode(e.target.value)} testid="otp-code" required />
              <button type="submit" disabled={busy || code.length < 4} data-testid="otp-submit"
                className="w-full bg-bb-gold text-bb-bg hover:bg-bb-champagne transition-colors rounded-sm py-2.5 font-medium disabled:opacity-60">
                {busy ? "Verifying…" : "Verify & Continue"}
              </button>
              <button type="button" onClick={() => setOtpStep(false)} data-testid="otp-back"
                className="w-full text-bb-muted text-xs hover:text-bb-gold transition-colors">Back</button>
            </form>
          ) : (
          <form onSubmit={submit} className="space-y-4">
            {isRegister && (
              <>
                <Field label="Full Name" value={form.full_name} onChange={upd("full_name")} testid="reg-name" required />
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Company" value={form.company} onChange={upd("company")} testid="reg-company" />
                  <Field label="Country" value={form.country} onChange={upd("country")} testid="reg-country" />
                </div>
                <Field label={`Mobile Number ${smsEnabled ? "(required, e.g. +15551234567)" : "(optional, e.g. +15551234567)"}`}
                  type="tel" value={form.phone} onChange={upd("phone")} testid="reg-phone" required={smsEnabled} />
              </>
            )}
            <Field label="Email" type="email" value={form.email} onChange={upd("email")} testid="auth-email" required />
            <Field label="Password" type="password" value={form.password} onChange={upd("password")} testid="auth-password" required />
            {!isRegister && (
              <div className="text-right -mt-1">
                <Link to="/forgot-password" data-testid="forgot-password-link"
                  className="text-xs text-bb-champagne hover:text-bb-gold transition-colors">Forgot password?</Link>
              </div>
            )}

            {isRegister && (
              <div>
                <label className="text-[11px] uppercase tracking-wider text-bb-muted">Membership Tier</label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {tiers.map(([t, p]) => (
                    <button type="button" key={t} onClick={() => setTier(t)} data-testid={`tier-select-${t}`}
                      className={`rounded-sm border px-2 py-2 text-xs capitalize transition-colors ${tier === t ? "border-bb-gold text-bb-champagne bg-bb-gold/15" : "border-white/10 text-bb-muted hover:border-white/25"}`}>
                      <div>{t}</div><div className="tnum text-[10px] mt-0.5">{p}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <button type="submit" disabled={busy} data-testid="auth-submit"
              className="w-full bg-bb-gold text-bb-bg hover:bg-bb-champagne transition-colors rounded-sm py-2.5 font-medium disabled:opacity-60">
              {busy ? "Please wait…" : isRegister ? "Start Free Trial" : "Sign In"}
            </button>
          </form>
          )}
          <div className="text-center text-sm text-bb-muted mt-6">
            {isRegister ? (
              <>Already a member? <Link to="/login" className="text-bb-gold hover:underline">Sign in</Link></>
            ) : (
              <>No account? <Link to="/register" className="text-bb-gold hover:underline">Start free trial</Link></>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, testid, required }) {
  const [show, setShow] = useState(false);
  const isPw = type === "password";
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider text-bb-muted">{label}</label>
      <div className="relative mt-1.5">
        <input type={isPw && show ? "text" : type} value={value} onChange={onChange} required={required} data-testid={testid}
          className={`w-full bg-bb-surface border border-white/10 focus:border-bb-gold outline-none rounded-sm px-3 py-2 text-sm text-bb-text placeholder:text-bb-muted transition-colors ${isPw ? "pr-10" : ""}`} />
        {isPw && (
          <button type="button" onClick={() => setShow(!show)} data-testid={`${testid}-toggle`}
            aria-label={show ? "Hide password" : "Show password"}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-bb-muted hover:text-bb-gold transition-colors">
            {show ? <EyeOff size={15} /> : <Eye size={15} />}
          </button>
        )}
      </div>
    </div>
  );
}
